import Alpine from 'alpinejs';
import { normalize } from '../utils/helpers.js';
import { fetchGeocodingSuggestions } from '../utils/mapboxGeocoding.js';
import { resolveComposerDestination } from '../utils/searchUrlState.js';

const SECTION_PAGE_SIZE = 4;

Alpine.data('whereAutocomplete', (config = {}) => ({
    craftLocations: config.craftLocations || [],
    composerMode: config.composer === true,
    suggestions: [],
    rentalSuggestions: [],
    rentalHasMore: false,
    loading: false,
    loadingRentals: false,
    highlightedIndex: -1,
    abortController: null,
    rentalAbortController: null,
    _debounceTimer: null,
    _isSelecting: false,
    visibleDestinationLimit: SECTION_PAGE_SIZE,
    visibleMarketLimit: SECTION_PAGE_SIZE,
    visibleResortLimit: SECTION_PAGE_SIZE,
    visibleGeolocationLimit: SECTION_PAGE_SIZE,

    get selectedDestination() {
        return resolveComposerDestination(
            Alpine.store('search'),
            this.craftLocations
        );
    },

    openPopover() {
        if (this._isSelecting) return;

        // Don't open on focus/click if the field already has content —
        // only handleInput (actual typing) should reopen the popover.
        const query = Alpine.store('search').where;
        if (query && query.length > 0) return;

        const popoverKey =
            this.$root.closest('[data-popover-key]')?.dataset?.popoverKey;
        if (popoverKey) Alpine.store('popovers').open(popoverKey);
    },

    handleInput() {
        if (this._isSelecting) return;

        const popoverKey =
            this.$root.closest('[data-popover-key]')?.dataset?.popoverKey;
        if (popoverKey) Alpine.store('popovers').open(popoverKey);

        Alpine.store('search').whereData = null;
        Alpine.store('search').composerDestinationSlug = null;
        this.resetVisibleLimits();

        clearTimeout(this._debounceTimer);
        const query = Alpine.store('search').where;
        this.cancelSuggestionRequests();
        this.suggestions = [];
        this.rentalSuggestions = [];
        this.rentalHasMore = false;
        this.highlightedIndex = -1;

        if (!query || query.length < 2) {
            return;
        }

        this._debounceTimer = setTimeout(
            () => this._fetchSuggestions(query),
            300
        );
    },

    // --- Computed getters ---

    get directlyMatchingCraftEntries() {
        const query = Alpine.store('search').where;
        return this.craftLocations.filter((entry) =>
            Alpine.store('search').matches(entry.title, query)
        );
    },

    // Retained for the explicit legacy hero-form override.
    get filteredCraftEntries() {
        return this.directlyMatchingCraftEntries;
    },

    get filteredMarkets() {
        const entries = this.filteredMarketEntries;

        return this.composerMode
            ? entries.filter((entry) => !entry.isResort)
            : entries;
    },

    get filteredResorts() {
        if (!this.composerMode) return [];

        return this.filteredMarketEntries.filter((entry) => entry.isResort);
    },

    get filteredMarketEntries() {
        // Read once: this getter rescans all locations on every access.
        const matchingEntries = this.directlyMatchingCraftEntries;
        const destinationSlugs = new Set(
            matchingEntries
                .filter((entry) => entry.type === 'destination')
                .map((entry) => entry.slug)
        );

        return this.craftLocations.filter(
            (entry) =>
                entry.type === 'market' &&
                (matchingEntries.includes(entry) ||
                    destinationSlugs.has(entry.parentSlug))
        );
    },

    get filteredDestinations() {
        return this.directlyMatchingCraftEntries.filter(
            (e) => e.type === 'destination'
        );
    },

    get visibleDestinations() {
        return this.composerMode
            ? this.filteredDestinations.slice(0, this.visibleDestinationLimit)
            : this.filteredDestinations;
    },

    get visibleMarkets() {
        return this.composerMode
            ? this.filteredMarkets.slice(0, this.visibleMarketLimit)
            : this.filteredMarkets;
    },

    get visibleResorts() {
        return this.filteredResorts.slice(0, this.visibleResortLimit);
    },

    get visibleGeolocationSuggestions() {
        return this.suggestions.slice(0, this.visibleGeolocationLimit);
    },

    get hasMoreDestinations() {
        return (
            this.visibleDestinations.length < this.filteredDestinations.length
        );
    },

    get hasMoreMarkets() {
        return this.visibleMarkets.length < this.filteredMarkets.length;
    },

    get hasMoreResorts() {
        return this.visibleResorts.length < this.filteredResorts.length;
    },

    get hasMoreGeolocationSuggestions() {
        return (
            this.visibleGeolocationSuggestions.length < this.suggestions.length
        );
    },

    get visibleCraftEntries() {
        return this.composerMode
            ? [
                  ...this.visibleDestinations,
                  ...this.visibleMarkets,
                  ...this.visibleResorts
              ]
            : [...this.filteredMarkets, ...this.filteredDestinations];
    },

    get selectedDestinationMarkets() {
        if (!this.selectedDestination) return [];

        return this.craftLocations.filter(
            (entry) =>
                entry.type === 'market' &&
                entry.parentSlug === this.selectedDestination.slug
        );
    },

    get cappedMapboxSuggestions() {
        if (this.composerMode) return this.visibleGeolocationSuggestions;

        const limit = Math.max(0, 10 - this.visibleCraftEntries.length);
        return this.suggestions.slice(0, limit);
    },

    // Used for empty-state detection, not an inventory total; loaded rentals are sufficient.
    get totalItems() {
        if (!this.composerMode) {
            return (
                this.filteredDestinations.length +
                this.filteredMarkets.length +
                this.cappedMapboxSuggestions.length +
                this.rentalSuggestions.length
            );
        }

        return (
            this.filteredDestinations.length +
            this.filteredMarkets.length +
            this.filteredResorts.length +
            this.suggestions.length +
            this.rentalSuggestions.length
        );
    },

    get visibleItemCount() {
        return (
            this.visibleCraftEntries.length +
            this.cappedMapboxSuggestions.length +
            this.rentalSuggestions.length
        );
    },

    resultIndex(group, index) {
        const offsets = {
            destination: 0,
            market: this.visibleDestinations.length,
            resort:
                this.visibleDestinations.length + this.visibleMarkets.length,
            geolocation:
                this.visibleDestinations.length +
                this.visibleMarkets.length +
                this.visibleResorts.length,
            rental:
                this.visibleDestinations.length +
                this.visibleMarkets.length +
                this.visibleResorts.length +
                this.cappedMapboxSuggestions.length
        };

        return (offsets[group] ?? 0) + index;
    },

    loadMoreDestinations() {
        this.visibleDestinationLimit += SECTION_PAGE_SIZE;
    },

    loadMoreMarkets() {
        this.visibleMarketLimit = this.filteredMarkets.length;
    },

    loadMoreResorts() {
        this.visibleResortLimit += SECTION_PAGE_SIZE;
    },

    loadMoreGeolocationSuggestions() {
        this.visibleGeolocationLimit += SECTION_PAGE_SIZE;
    },

    loadMoreRentals() {
        if (this.loadingRentals || !this.rentalHasMore) return;

        this._fetchRentalSuggestions(Alpine.store('search').where, {
            append: true
        });
    },

    resetVisibleLimits() {
        this.visibleDestinationLimit = SECTION_PAGE_SIZE;
        this.visibleMarketLimit = SECTION_PAGE_SIZE;
        this.visibleResortLimit = SECTION_PAGE_SIZE;
        this.visibleGeolocationLimit = SECTION_PAGE_SIZE;
    },

    cancelSuggestionRequests() {
        if (this.abortController) this.abortController.abort();
        if (this.rentalAbortController) this.rentalAbortController.abort();
        this.abortController = null;
        this.rentalAbortController = null;
        this.loading = false;
        this.loadingRentals = false;
    },

    clearSelection() {
        this.cancelSuggestionRequests();
        Alpine.store('search').composerDestinationSlug = null;
        this.resetVisibleLimits();
        this.suggestions = [];
        this.rentalSuggestions = [];
        this.rentalHasMore = false;
        this.highlightedIndex = -1;
    },

    selectDestinationScope() {
        if (!this.selectedDestination) return;

        const { title, lat, lng, slug } = this.selectedDestination;
        const store = Alpine.store('search');
        store.composerDestinationSlug = slug;
        store.where = title;
        store.whereData = {
            lat: lat ?? null,
            lng: lng ?? null,
            slug,
            type: 'destination'
        };
        if (this.composerMode) {
            this.advanceAfterLocationSelection();
        }
    },

    selectDestinationMarket(entry) {
        if (!this.selectedDestination || !entry) return;

        const store = Alpine.store('search');
        store.composerDestinationSlug = this.selectedDestination.slug;
        store.where = entry.title;
        store.whereData = {
            lat: entry.lat ?? null,
            lng: entry.lng ?? null,
            slug: entry.slug,
            type: entry.type || 'market'
        };
        store.ui.validationMessage = null;
        this.highlightedIndex = -1;
        if (this.composerMode) {
            this.advanceAfterLocationSelection();
        }
    },

    // --- Fetch methods ---

    async _fetchSuggestions(query) {
        if (this.abortController) this.abortController.abort();
        const controller = new AbortController();
        this.abortController = controller;

        // Build dedup set from craft locations
        const craftTitles = new Set(
            this.craftLocations.map((e) => normalize(e.title))
        );

        this.loading = true;
        this._fetchRentalSuggestions(query);

        try {
            const results = await fetchGeocodingSuggestions(
                query,
                controller.signal
            );
            if (this.abortController !== controller) return;

            this.suggestions = results.filter(
                (s) => !craftTitles.has(normalize(s.name))
            );
        } catch (e) {
            if (e.name !== 'AbortError') console.error('Geocoding error:', e);
        } finally {
            if (this.abortController === controller) {
                this.abortController = null;
                this.loading = false;
            }
        }
    },

    async _fetchRentalSuggestions(query, { append = false } = {}) {
        if (this.rentalAbortController) this.rentalAbortController.abort();
        const controller = new AbortController();
        this.rentalAbortController = controller;
        const offset = append ? this.rentalSuggestions.length : 0;

        if (!append) {
            this.rentalSuggestions = [];
            this.rentalHasMore = false;
        }

        this.loadingRentals = true;

        try {
            const params = new URLSearchParams({
                q: query.trim(),
                limit: String(SECTION_PAGE_SIZE),
                offset: String(offset)
            });
            const response = await fetch(
                `/actions/guesty/search/autocomplete?${params.toString()}`,
                { signal: controller.signal }
            );

            if (this.rentalAbortController !== controller) return;

            if (!response.ok) {
                if (!append) this.rentalSuggestions = [];
                return;
            }

            const data = await response.json();
            if (this.rentalAbortController !== controller) return;

            const results =
                data.success && Array.isArray(data.results) ? data.results : [];

            this.rentalSuggestions = append
                ? [
                      ...this.rentalSuggestions,
                      ...results.filter(
                          (result) =>
                              !this.rentalSuggestions.some(
                                  (existing) => existing.slug === result.slug
                              )
                      )
                  ]
                : results;
            this.rentalHasMore = data.hasMore === true;
        } catch (e) {
            if (e.name !== 'AbortError')
                console.error('Rental autocomplete error:', e);
            if (!append) this.rentalSuggestions = [];
        } finally {
            if (this.rentalAbortController === controller) {
                this.rentalAbortController = null;
                this.loadingRentals = false;
            }
        }
    },

    // --- Selection methods ---

    selectCraftEntry(
        title,
        lat,
        lng,
        slug,
        type = null,
        advance = true,
        forceAdvance = false
    ) {
        const store = Alpine.store('search');
        this._isSelecting = true;
        this.cancelSuggestionRequests();
        store.composerDestinationSlug = type === 'destination' ? slug : null;

        store.where = title;
        store.whereData =
            lat != null && lng != null
                ? { lat, lng, slug, type }
                : { lat: null, lng: null, slug, type };

        this.suggestions = [];
        this.rentalSuggestions = [];
        this.rentalHasMore = false;
        this.highlightedIndex = -1;
        this._closePopover();
        this.$root
            .querySelectorAll('input[name="where"]')
            .forEach((input) => input.blur());

        this.$nextTick(() => {
            this._isSelecting = false;
            if (
                this.composerMode &&
                advance &&
                (type !== 'destination' || forceAdvance)
            ) {
                this.advanceAfterLocationSelection();
            }
        });
    },

    selectMapboxSuggestion(suggestion) {
        const store = Alpine.store('search');
        this._isSelecting = true;
        this.cancelSuggestionRequests();
        store.composerDestinationSlug = null;

        store.where = suggestion.fullName;
        store.whereData = {
            lat: suggestion.lat,
            lng: suggestion.lng,
            placeType: suggestion.placeType,
            name: suggestion.name
        };

        this.highlightedIndex = -1;
        this._closePopover();

        setTimeout(() => {
            this.suggestions = [];
            this.rentalSuggestions = [];
            this.rentalHasMore = false;
            this._isSelecting = false;
            if (this.composerMode) {
                this.advanceAfterLocationSelection();
            }
        }, 50);
    },

    rentalSuggestionUrl(rental) {
        if (rental?.url) return rental.url;
        if (!rental?.slug) return null;

        return `/listing/${encodeURIComponent(rental.slug)}`;
    },

    selectRentalSuggestion(rental, event = null) {
        // Keep the clicked anchor in the DOM until its native navigation runs.
        // Clearing the suggestions during the click can delay that navigation.
        if (event) return;

        const store = Alpine.store('search');
        this.cancelSuggestionRequests();
        store.composerDestinationSlug = null;
        store.where = rental.name;
        store.whereData = null;
        this.suggestions = [];
        this.rentalSuggestions = [];
        this.rentalHasMore = false;
        this.highlightedIndex = -1;
        this._closePopover();

        const url = this.rentalSuggestionUrl(rental);
        if (url) window.location.href = url;
    },

    // --- Keyboard navigation ---

    handleKeydown(event) {
        const popoverKey =
            this.$root.closest('[data-popover-key]')?.dataset?.popoverKey;
        const isOpen =
            popoverKey && Alpine.store('popovers').isOpen(popoverKey);

        if (!isOpen) return;

        const total = this.visibleItemCount;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.highlightedIndex =
                (this.highlightedIndex + 1) % Math.max(total, 1);
            this._scrollToHighlighted();
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.highlightedIndex =
                this.highlightedIndex <= 0
                    ? Math.max(total - 1, 0)
                    : this.highlightedIndex - 1;
            this._scrollToHighlighted();
        } else if (event.key === 'Enter') {
            event.preventDefault();
            this._selectHighlighted();
        } else if (event.key === 'Escape') {
            this.cancelSuggestionRequests();
            this.suggestions = [];
            this.rentalSuggestions = [];
            this.rentalHasMore = false;
            this.highlightedIndex = -1;
        }
    },

    _selectHighlighted() {
        const craftEntries = this.visibleCraftEntries;
        const craftCount = craftEntries.length;
        const mapboxItems = this.cappedMapboxSuggestions;
        const mapboxCount = mapboxItems.length;
        const rentals = this.rentalSuggestions;

        const idx = this.highlightedIndex >= 0 ? this.highlightedIndex : 0;
        const total = craftCount + mapboxCount + rentals.length;

        if (total === 0) return;

        if (idx < craftCount) {
            const entry = craftEntries[idx];
            this.selectCraftEntry(
                entry.title,
                entry.lat,
                entry.lng,
                entry.slug,
                entry.type
            );
        } else if (idx < craftCount + mapboxCount) {
            const mapboxIdx = idx - craftCount;
            this.selectMapboxSuggestion(mapboxItems[mapboxIdx]);
        } else {
            const rentalIdx = idx - craftCount - mapboxCount;
            if (rentals[rentalIdx]) {
                this.selectRentalSuggestion(rentals[rentalIdx]);
            }
        }
    },

    _scrollToHighlighted() {
        this.$nextTick(() => {
            const el = this.$root.querySelector('.is-highlighted');
            const container = this.$root.querySelector('.form-popover--where');
            if (!el || !container) return;

            const elRect = el.getBoundingClientRect();
            const cRect = container.getBoundingClientRect();

            if (elRect.bottom > cRect.bottom) {
                container.scrollTop += elRect.bottom - cRect.bottom;
            } else if (elRect.top < cRect.top) {
                container.scrollTop -= cRect.top - elRect.top;
            }
        });
    },

    _closePopover() {
        const popoverKey =
            this.$root.closest('[data-popover-key]')?.dataset?.popoverKey;
        if (popoverKey) Alpine.store('popovers').close(popoverKey);
    },

    destroy() {
        clearTimeout(this._debounceTimer);
        if (this.abortController) this.abortController.abort();
        if (this.rentalAbortController) this.rentalAbortController.abort();
    }
}));
